/* ============================================================================
   AKINOD — Comportements transverses
   ----------------------------------------------------------------------------
   Quatre briques réutilisées par toutes les pages : l'apparition au défilement,
   les carrousels horizontaux glissables (flèches et puces comprises), la nav qui
   se compacte et le bouton de retour en haut.

   Tout est idempotent : le thème peut réinjecter une section depuis l'éditeur
   sans doubler les écouteurs. Chaque comportement pose SON PROPRE marquage —
   `data-ak-reveal`, `data-ak-scroll`, `data-ak-nav`, `data-ak-top`. Un marquage
   unique partagé serait un piège : un même élément porte souvent deux rôles
   (la bande de « Produits de saison » est à la fois `.ak-reveal` et
   `.ak-scroller`), et le premier comportement amorcé condamnerait le second.
   ========================================================================== */

(() => {
  'use strict';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- Qui défile, au juste ? ---------------------------------------------
     Au-dessus de 990 px, Horizon met `html` et `body` en `overflow: hidden` et
     confie le défilement à `.page-wrapper`. La fenêtre ne bouge donc plus :
     `window.scrollY` reste à zéro et un écouteur posé sur `window` ne se
     déclenche jamais. En dessous de 990 px, c'est la page qui défile de
     nouveau. On vise le bon élément à chaque lecture, et on écoute les deux —
     la bascule se fait au redimensionnement, sans rechargement. */

  const pageWrapper = () => document.querySelector('.page-wrapper');

  function scrollerEl() {
    const pw = pageWrapper();
    if (pw && pw.scrollHeight > pw.clientHeight + 1) return pw;
    return document.scrollingElement || document.documentElement;
  }

  const scrollY = () => scrollerEl().scrollTop;

  /** Écoute le défilement, d'où qu'il vienne. */
  function onScroll(handler) {
    const relais = () => handler();
    window.addEventListener('scroll', relais, { passive: true });
    pageWrapper()?.addEventListener('scroll', relais, { passive: true });
  }

  /* --- Apparition au défilement ------------------------------------------ */

  function initReveal(root = document) {
    const items = root.querySelectorAll('.ak-reveal:not([data-ak-reveal])');
    if (!items.length) return;

    // Sans IntersectionObserver, ou en mouvement réduit, on montre tout d'emblée
    // plutôt que de laisser du contenu invisible.
    if (REDUCED || !('IntersectionObserver' in window)) {
      items.forEach((el) => { el.dataset.akReveal = '1'; el.classList.add('is-visible'); });
      return;
    }

    /* Filet de sécurité. L'apparition est un ornement ; le contenu, non. Or
       `IntersectionObserver` ne rend pas la main partout : onglet en arrière-plan,
       page rendue hors écran, navigateur piloté — dans ces cas il n'émet jamais
       la moindre entrée, et TOUT le contenu marqué reste à `opacity: 0`. Mesuré :
       15 éléments sur 15 invisibles, y compris un titre en haut de page.

       On note donc si l'observateur a répondu au moins une fois. Sinon, on
       découvre tout. Mieux vaut une page sans animation qu'une page blanche. */
    let aRepondu = false;

    const io = new IntersectionObserver((entries) => {
      aRepondu = true;
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        e.target.classList.add('is-visible');
        io.unobserve(e.target);          // une seule fois : pas de va-et-vient au scroll
      }
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });

    items.forEach((el) => { el.dataset.akReveal = '1'; io.observe(el); });

    setTimeout(() => {
      if (aRepondu) return;
      io.disconnect();
      items.forEach((el) => el.classList.add('is-visible'));
    }, 1200);
  }

  /* --- Carrousel horizontal glissable ------------------------------------- */

  function initScrollers(root = document) {
    root.querySelectorAll('.ak-scroller:not([data-ak-scroll])').forEach((el) => {
      el.dataset.akScroll = '1';

      /* --- Glisser à la souris : sur demande seulement -----------------------
         Le glisser n'est PAS le comportement par défaut d'un carrousel. Une
         rangée de produits se traîne volontiers à la souris — elle annonce
         d'ailleurs `cursor: grab`. Le mini-carrousel d'une carte produit, lui,
         se parcourt aux flèches : y activer le glisser fait suivre l'image sous
         le curseur et découvre une lisière de la diapositive suivante au bord
         droit, ce qui se lit comme une bordure parasite.

         La distinction ne peut pas se déduire du CSS — `cursor` est hérité, et
         la carte tient son `grab` de la rangée qui la contient. Elle est donc
         déclarée : `data-ak-drag` sur les bandes qui le veulent.

         Poser le glisser sur la rangée et non sur la carte reste juste : le
         `pointerdown` remonte, saisir une carte fait donc glisser la rangée. */

      if (el.hasAttribute('data-ak-drag')) {
        let startX = 0, startScroll = 0, dragging = false, moved = 0, capture = false;
        let vientDeGlisser = false;

        /* Seuil au-delà duquel le geste est un glissement et non un clic. */
        const SEUIL = 8;

        el.addEventListener('pointerdown', (e) => {
          // On ne détourne pas le geste tactile : le scroll natif est meilleur.
          if (e.pointerType !== 'mouse') return;
          dragging = true; moved = 0; capture = false;
          startX = e.clientX;
          startScroll = el.scrollLeft;
          el.classList.add('is-dragging');
          /* Surtout PAS de `setPointerCapture` ici. Tant que la capture est
             active, le navigateur adresse le `click` à l'élément capteur — la
             bande — et non à ce qui se trouve sous le curseur. Les liens des
             cartes ne s'ouvraient donc plus et leurs flèches ne répondaient
             plus, mais seulement sur les rangées glissables : ailleurs, faute
             de `data-ak-drag`, il n'y a pas de capture. On ne capture qu'une
             fois le glissement avéré, quand il n'y a plus de clic à préserver. */
        });

        el.addEventListener('pointermove', (e) => {
          if (!dragging) return;
          const dx = e.clientX - startX;
          moved = Math.max(moved, Math.abs(dx));

          if (!capture && moved > SEUIL) {
            capture = true;
            try { el.setPointerCapture(e.pointerId); } catch { /* pointeur déjà parti */ }
          }
          if (!capture) return;   // simple frémissement : on laisse le clic vivre

          el.scrollLeft = startScroll - dx;
        });

        const end = (e) => {
          if (!dragging) return;
          dragging = false;
          el.classList.remove('is-dragging');
          if (capture) { try { el.releasePointerCapture(e.pointerId); } catch { /* déjà relâché */ } }
          capture = false;

          /* Un seul clic est à neutraliser : celui que le navigateur émet à la
             fin du glissement. Au-delà, la garde doit se taire. Elle testait
             `moved`, qui n'est remis à zéro qu'au `pointerdown` suivant : un
             clic sans appui préalable — activation au clavier sur un lien ou un
             bouton de la bande — se faisait donc avaler tant que personne
             n'avait re-cliqué. Un drapeau à usage unique règle les deux cas. */
          vientDeGlisser = moved > SEUIL;
          moved = 0;
        };
        el.addEventListener('pointerup', end);
        el.addEventListener('pointercancel', end);

        // Un glissement ne doit pas déclencher le lien sous le curseur.
        el.addEventListener('click', (e) => {
          if (!vientDeGlisser) return;
          vientDeGlisser = false;
          e.preventDefault();
          e.stopPropagation();
        }, true);
      }

      /* --- Flèches ---------------------------------------------------------
         Déclarées via data-ak-scroller-prev / -next, dans le cadre porteur.

         On ne défile pas d'un pas fixe : un pas fixe finit par couper une carte
         en deux, et surtout il vise une position que l'alignement obligatoire
         (`scroll-snap-type: x mandatory`) devra corriger juste après. On vise
         donc directement un point d'ancrage — le début d'une carte, décalé du
         rembourrage d'alignement. La bande s'arrête là où elle se serait calée
         d'elle-même, et l'alignement n'a plus rien à rattraper. */

      /* Un cadre en contient d'autres : `.ak-prow` porte la rangée ET les
         cartes, qui ont chacune le leur. Un `querySelector` sur le cadre de la
         rangée rendait donc la flèche de la PREMIÈRE CARTE — mesuré : cinq
         boutons « précédent » dans un même cadre, et c'est une carte qui pilotait
         la rangée. On ne retient que les flèches dont le cadre le plus proche est
         bien le nôtre. */
      const scope = el.closest('[data-ak-scroller-scope]') || el.parentElement;
      const flechesDeCeCadre = (sel) => [...(scope?.querySelectorAll(sel) ?? [])]
        .filter((b) => b.closest('[data-ak-scroller-scope]') === scope);

      /** Le retrait d'alignement de la bande, en pixels.

         Une bande peut déclarer `scroll-padding-inline-start` : l'alignement se
         fait alors sur une ligne posée à l'intérieur du cadre, et la position de
         calage d'une carte vaut son décalage MOINS ce retrait. Sans cette
         déclaration (valeur `auto`), la carte se cale sur le bord du cadre et la
         position de calage vaut son décalage tout court.

         Les deux cas cohabitent dans ce thème, et c'est là qu'était le défaut :
         la bande « produits de saison » déclare le retrait, les rangées
         d'univers non. Mesuré le 21/09 à 1920 px sur la saison : décalages des
         cartes [278, 680, 1082, 1484, 1886, 2288], retrait 278, donc calages
         réels [0, 402, 804, 1026 (fin de course)]. Le calcul d'avant prenait les
         décalages pour des calages : la plus petite cible valait 278, jamais 0,
         et la flèche « précédent » ne revenait pas au début.

         La valeur calculée du retrait reste parfois une expression non résolue
         (`max(28px, 50% - 682px)`), que `parseFloat` rend en NaN. Dans ce thème
         un retrait déclaré vaut toujours le rembourrage de la bande elle-même
         (même variable) : on lit donc ce rembourrage, qui est résolu. */
      const retraitAlignement = () => {
        const cs = getComputedStyle(el);
        const brut = cs.scrollPaddingInlineStart && cs.scrollPaddingInlineStart !== 'auto'
          ? cs.scrollPaddingInlineStart
          : cs.scrollPaddingLeft;
        if (!brut || brut === 'auto') return 0;
        const px = /^\s*(-?[\d.]+)px\s*$/.exec(brut);
        if (px) return parseFloat(px[1]);
        return parseFloat(cs.paddingInlineStart || cs.paddingLeft) || 0;
      };

      /** Les positions de défilement où une carte vient se caler.

         Bornées à la course réelle : au-delà, la bande ne bouge plus et l'on
         viserait un point que le navigateur ramènerait de toute façon à la fin.
         Les rangs sont conservés — les puces s'y réfèrent par leur index. */
      const ancrages = () => {
        const gauche = el.getBoundingClientRect().left;
        const retrait = retraitAlignement();
        const course = Math.max(0, el.scrollWidth - el.clientWidth);
        return [...el.children].map((c) => {
          const x = c.getBoundingClientRect().left - gauche + el.scrollLeft - retrait;
          return Math.max(0, Math.min(course, Math.round(x)));
        });
      };

      /* --- Les puces suivent la vue affichée --------------------------------
         Elles étaient posées en Liquid, la première allumée, et n'ont jamais
         bougé ensuite : sur quatre vues on montrait toujours « première sur
         quatre ». On les rallume d'après la position réelle du défilement.

         Deux déclencheurs, et c'est volontaire : l'évènement `scroll` couvre le
         glissement tactile et la molette ; l'appel direct depuis `paginer()`
         couvre les flèches sans dépendre de cet évènement — mesuré, un
         navigateur piloté peut déplacer la bande sans en émettre un seul.
         Purement indicatives, les puces ne captent pas le pointeur. */

      const puces = flechesDeCeCadre('[data-ak-dots]')[0];

      /** Rallume la puce de la vue `i`, ou celle déduite de la position. */
      function allumerPuce(i) {
        if (!puces || !puces.children.length) return;
        let vue = i;
        if (vue == null) {
          const points = ancrages();
          if (!points.length) return;
          // La vue la plus proche, et non un arrondi : les vues n'ont pas
          // toutes la même largeur d'une carte à l'autre.
          vue = 0;
          for (let k = 1; k < points.length; k += 1) {
            if (Math.abs(points[k] - el.scrollLeft) < Math.abs(points[vue] - el.scrollLeft)) vue = k;
          }
        }
        [...puces.children].forEach((p, k) => p.classList.toggle('is-on', k === vue));
      }

      if (puces) {
        el.addEventListener('scroll', () => allumerPuce(null), { passive: true });
        allumerPuce(null);
      }

      function paginer(sens) {
        const pas = Math.max(el.clientWidth * 0.8, 240);
        const vise = el.scrollLeft + sens * pas;
        const max = el.scrollWidth - el.clientWidth;

        // L'ancrage le plus proche de la cible, mais jamais celui d'où l'on
        // part : sur une carte plus large que le pas, on n'avancerait pas.
        const points = ancrages().filter((x) => (sens > 0 ? x > el.scrollLeft + 1 : x < el.scrollLeft - 1));
        const cible = points.length
          ? points.reduce((a, b) => (Math.abs(b - vise) < Math.abs(a - vise) ? b : a))
          : Math.max(0, Math.min(max, vise));

        /* Ceinture et bretelles. Le défilement doux n'aboutit pas dans tous les
           contextes — moteur qui l'ignore sur une bande à alignement, navigateur
           piloté, réglage système —, et la flèche paraît alors morte. Ça ne se
           détecte pas à l'avance : on vérifie après coup et on refait le trajet
           sèchement si rien n'a bougé. Mieux vaut un saut qu'un bouton sans
           effet. Constaté en conditions de test : `auto` déplaçait la bande là
           où `smooth` la laissait sur place. */
        const depart = el.scrollLeft;
        el.scrollTo({ left: cible, behavior: REDUCED ? 'auto' : 'smooth' });

        // La cible est connue : on rallume la puce sans attendre l'animation.
        allumerPuce(ancrages().indexOf(cible) >= 0 ? ancrages().indexOf(cible) : null);

        if (REDUCED) return;
        setTimeout(() => {
          if (Math.abs(el.scrollLeft - depart) < 2) el.scrollTo({ left: cible, behavior: 'auto' });
        }, 120);
      }

      flechesDeCeCadre('[data-ak-scroller-prev]').forEach((b) => b.addEventListener('click', () => paginer(-1)));
      flechesDeCeCadre('[data-ak-scroller-next]').forEach((b) => b.addEventListener('click', () => paginer(1)));


    });
  }

  /* --- Nav compactée au défilement ---------------------------------------- */

  function initStickyNav() {
    const nav = document.querySelector('[data-ak-nav]');
    if (!nav || nav.dataset.akNav) return;
    nav.dataset.akNav = '1';

    let ticking = false;
    const apply = () => {
      nav.classList.toggle('is-scrolled', scrollY() > 30);
      ticking = false;
    };
    onScroll(() => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    });
    apply();
  }

  /* --- Bouton retour haut -------------------------------------------------- */

  function initToTop() {
    const btn = document.querySelector('[data-ak-to-top]');
    if (!btn || btn.dataset.akTop) return;
    btn.dataset.akTop = '1';

    btn.addEventListener('click', () => {
      scrollerEl().scrollTo({ top: 0, behavior: REDUCED ? 'auto' : 'smooth' });
    });

    let ticking = false;
    const apply = () => {
      btn.classList.toggle('is-visible', scrollY() > window.innerHeight * 1.5);
      ticking = false;
    };
    onScroll(() => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    });
    apply();
  }

  /* --- Amorçage ------------------------------------------------------------ */

  function boot(root) {
    initReveal(root);
    initScrollers(root);
    initStickyNav();
    initToTop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(document));
  } else {
    boot(document);
  }

  // L'éditeur de thème réinjecte les sections sans recharger la page.
  document.addEventListener('shopify:section:load', (e) => boot(e.target));

  window.AkinodBehaviors = { boot };
})();
